// GRN phase 1 (2026-10-21, g02): Create PO asks how it was bought, every time, with nothing pre-selected.
// "Order from vendor" asks the expected delivery date (g11), filled from how long the vendor usually takes.
// 2026-10-22, per the approved mockup: on laptops the question is a section of the Create Purchase Order page
// (PathPicker inside POCreatePathStep); phones keep it as a bottom sheet (PathChoice).
import React, { useEffect, useState } from 'react';
import { Button, DatePicker, Drawer, Input, Modal } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { getVendorLeadTime, LeadTime } from '../../api';
import { useCompactLayout } from '../../hooks/useCompactLayout';

export type PurchasePath = 'direct' | 'order';

const MUTED = '#5f6672';
const fieldLabel: React.CSSProperties = { fontWeight: 600, display: 'block', marginBottom: 6 };

/** The two path cards and, for a vendor order, the expected delivery date (and notes on the page). Controlled. */
export const PathPicker: React.FC<{
  vendorId: number | null;
  vendorName: string;
  path: PurchasePath | null;
  onPath: (p: PurchasePath) => void;
  date: Dayjs | null;
  onDate: (d: Dayjs | null) => void;
  /** Page layout: cards side by side, date + notes on one row. Sheet: stacked. */
  layout: 'page' | 'sheet';
  notes?: string;
  onNotes?: (v: string) => void;
}> = ({ vendorId, vendorName, path, onPath, date, onDate, layout, notes, onNotes }) => {
  const [lead, setLead] = useState<LeadTime | null>(null);

  useEffect(() => {
    let live = true;
    setLead(null);
    if (vendorId) {
      getVendorLeadTime(vendorId)
        .then((lt) => { if (!live) return; setLead(lt); onDate(dayjs().add(lt.days, 'day')); })
        .catch(() => { if (live) onDate(dayjs().add(7, 'day')); });
    } else {
      onDate(dayjs().add(7, 'day'));
    }
    return () => { live = false; };
  }, [vendorId]); // eslint-disable-line react-hooks/exhaustive-deps

  const leadText = !lead
    ? ''
    : lead.basis === 'history'
      ? `${vendorName} usually delivers in ${lead.days} day${lead.days === 1 ? '' : 's'} (median of its last ${lead.orders} vendor order${lead.orders === 1 ? '' : 's'}). Change it if they promised another date.`
      : lead.basis === 'vendor'
        ? `${vendorName}'s usual lead time is ${lead.days} day${lead.days === 1 ? '' : 's'}. Change it if they promised another date.`
        : `No past vendor orders from ${vendorName}, so 7 days by default. Change it if they promised another date.`;

  const page = layout === 'page';
  const option = (value: PurchasePath, title: string, text: string) => {
    const on = path === value;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={on}
        onClick={() => onPath(value)}
        style={{
          textAlign: 'left', border: on ? '2px solid #1677ff' : '1px solid #d9d9d9', background: on ? '#e6f4ff' : '#fff',
          borderRadius: 10, padding: on ? 13 : 14, display: 'grid', gridTemplateColumns: '22px minmax(0, 1fr)', gap: 10,
          font: 'inherit', color: 'inherit', cursor: 'pointer', minHeight: 64, width: '100%',
        }}
      >
        <span aria-hidden style={{ width: 18, height: 18, borderRadius: '50%', boxSizing: 'border-box', border: on ? '6px solid #1677ff' : '2px solid #bfbfbf', marginTop: 2 }} />
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <b style={{ fontSize: page ? 15 : undefined }}>{title}</b>
          <span style={{ fontSize: 13, color: MUTED }}>{text}</span>
        </span>
      </button>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div role="radiogroup" aria-label="How was it bought?" style={{ display: 'grid', gridTemplateColumns: page ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: page ? 14 : 10 }}>
        {option('direct', 'Already bought · direct purchase', 'You paid and have the goods. Record the purchase and its receipt in one step.')}
        {option('order', 'Order from vendor · delivery later', 'The vendor delivers later. The PO waits in Expected deliveries until the goods arrive.')}
      </div>
      {!path && <span style={{ fontSize: 13, color: MUTED }}>Nothing is chosen for you. Pick one to continue.</span>}
      {path === 'order' && (
        <div style={{ display: 'grid', gridTemplateColumns: page && onNotes ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: 16 }}>
          <div>
            <label htmlFor="po-expected" style={fieldLabel}><span style={{ color: '#cf1322' }}>*</span> Expected delivery</label>
            <DatePicker
              id="po-expected"
              value={date}
              onChange={onDate}
              format="D MMM YYYY"
              allowClear={false}
              disabledDate={(d) => d.isBefore(dayjs(), 'day')}
              style={{ width: '100%' }}
              inputReadOnly={!page}
            />
            {leadText && <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>{leadText}</div>}
          </div>
          {page && onNotes && (
            <div>
              <label htmlFor="po-notes" style={fieldLabel}>Notes</label>
              <Input id="po-notes" value={notes} onChange={(e) => onNotes(e.target.value)} placeholder="Optional" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface Props {
  open: boolean;
  vendorId: number | null;
  vendorName: string;
  /** e.g. "2 lines · 8,500.00 EGP" */
  summary: string;
  busy?: boolean;
  onCancel: () => void;
  onDirect: () => void;
  onOrder: (expectedDelivery: string) => void;
}

/** Phones: the same question as a bottom sheet from the Create PO piles (phone mockup). */
const PathChoice: React.FC<Props> = ({ open, vendorId, vendorName, summary, busy, onCancel, onDirect, onOrder }) => {
  const compact = useCompactLayout();
  const [path, setPath] = useState<PurchasePath | null>(null);
  const [date, setDate] = useState<Dayjs | null>(null);

  useEffect(() => {
    if (!open) return;
    setPath(null); // g02: nothing pre-selected, every time
  }, [open]);

  const footer = (
    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
      <Button onClick={onCancel} style={compact ? { flex: 1, height: 44 } : undefined}>Cancel</Button>
      <Button
        type="primary"
        loading={busy}
        disabled={!path || (path === 'order' && !date)}
        onClick={() => (path === 'direct' ? onDirect() : onOrder(date!.format('YYYY-MM-DD')))}
        style={compact ? { flex: 1.4, height: 44 } : undefined}
      >
        {path === 'order' ? 'Create PO (Ordered)' : 'Continue'}
      </Button>
    </div>
  );
  const body = open ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 13, color: MUTED }}>{vendorName} · {summary}</div>
      <PathPicker vendorId={vendorId} vendorName={vendorName} path={path} onPath={setPath} date={date} onDate={setDate} layout="sheet" />
    </div>
  ) : null;

  if (compact) {
    return (
      <Drawer open={open} onClose={onCancel} placement="bottom" height="auto" title="How was it bought?" footer={footer} styles={{ body: { paddingBottom: 8 } }} destroyOnClose>
        {body}
      </Drawer>
    );
  }
  return (
    <Modal open={open} onCancel={onCancel} title="How was it bought?" footer={footer} width={520} destroyOnClose>
      {body}
    </Modal>
  );
};

export default PathChoice;
